/**
 * Slice 17f-D — resolve the external platform URL for a video post so the
 * indexed-card video tile can link out to the original platform (FB / Google
 * Business / Instagram) in a new tab.
 *
 * Pure function. No I/O. Used by `AdsAssistantAggregationPostsList` /
 * `AdsAssistantPostMediaStrip` when `media_type === "video"`.
 *
 * Fallback chain:
 *   1. `raw_payload.permalink_url` — already populated for Facebook Graph; we
 *      populate it for GHL Social Planner imports in this same slice.
 *   2. First entry of `media_urls` that looks like a recognizable post URL
 *      (FB / business.google.com / instagram.com) and not a raw media file.
 *      Last-resort heuristic for legacy rows that pre-date `permalink_url`.
 *   3. `null` → caller renders the video tile as a static poster (no click
 *      target).
 */
import type { AdsAssistantSocialPostRow } from "@/lib/adsAssistantClient";

const POST_LINK_HOST_SUFFIXES = ["facebook.com", "business.google.com", "instagram.com"] as const;

const MEDIA_FILE_EXTS = [
  ".mp4",
  ".mov",
  ".m4v",
  ".webm",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
] as const;

function parseHttpUrl(raw: string): URL | null {
  try {
    const u = new URL(raw);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    if (!u.hostname) return null;
    return u;
  } catch {
    return null;
  }
}

function looksLikeMediaFile(u: URL): boolean {
  const path = u.pathname.toLowerCase();
  return MEDIA_FILE_EXTS.some((ext) => path.endsWith(ext));
}

function looksLikePostUrl(u: URL): boolean {
  const host = u.hostname.toLowerCase();
  const hostMatches = POST_LINK_HOST_SUFFIXES.some(
    (suf) => host === suf || host.endsWith(`.${suf}`),
  );
  if (!hostMatches) return false;
  if (looksLikeMediaFile(u)) return false;
  return true;
}

/**
 * Resolve the external link-out URL for the given indexed social post.
 * Returns `null` when no usable external link can be derived from the row.
 */
export function resolveAdsPostExternalLink(post: AdsAssistantSocialPostRow): string | null {
  const permalink = post.raw_payload?.["permalink_url"];
  if (typeof permalink === "string" && permalink.length > 0) {
    const trimmed = permalink.trim();
    if (trimmed && parseHttpUrl(trimmed)) return trimmed;
  }
  if (Array.isArray(post.media_urls)) {
    for (const candidate of post.media_urls) {
      if (typeof candidate !== "string" || candidate.length === 0) continue;
      const trimmed = candidate.trim();
      if (!trimmed) continue;
      const u = parseHttpUrl(trimmed);
      if (!u) continue;
      if (looksLikePostUrl(u)) return trimmed;
    }
  }
  return null;
}
